import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { featuredVideos, categoryVideos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { brandInfo } from '@/mocks/brand';
import VideoCard from '@/components/feature/VideoCard';
import ArticleCard from '@/components/feature/ArticleCard';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function HomePage() {
  const { t } = useTranslation();

  const homeVideos = featuredVideos.slice(0, 8);
  const latestArticles = articles.slice(0, 3);
  const stats = brandInfo.stats;

  return (
    <>
      <SchemaOrg type="WebSite" data={{
        name: t('site.name'),
        url: 'https://huangtao.com',
        description: t('site.description'),
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://huangtao.com/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      }} />
      <SchemaOrg type="Organization" data={{
        name: t('site.name'),
        url: 'https://huangtao.com',
        description: t('site.description'),
        slogan: t('site.slogan'),
      }} />

      <section className="relative h-[500px] md:h-[620px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20dark%20cinematic%20background%20with%20abstract%20flowing%20curves%20in%20golden%20orange%20and%20deep%20charcoal%20tones%2C%20soft%20gradient%20glow%2C%20luxurious%20dark%20theater%20atmosphere%2C%20subtle%20film%20texture%20overlay%2C%20elegant%20and%20modern%20design%2C%20no%20text%20or%20people&width=1920&height=900&seq=hero-bg-home-01&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>

        <div className="relative z-10 w-full max-w-[1440px] mx-auto px-4 md:px-6">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight">
              <span className="font-display text-primary-400">{t('hero.title')}</span>
              <br />
              <span>{t('hero.subtitle')}</span>
            </h1>
            <p className="text-background-50/80 text-sm md:text-base mt-4 leading-relaxed max-w-lg">
              {t('hero.description')}
            </p>
            <div className="flex items-center gap-3 mt-6">
              <Link
                to="/category/dianshiju"
                className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 no-underline"
              >
                <i className="ri-play-circle-line mr-2"></i>{t('hero.cta')}
              </Link>
              <a
                href="#"
                className="whitespace-nowrap bg-background-50/15 hover:bg-background-50/25 backdrop-blur-sm text-background-50 border border-background-50/30 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 no-underline"
              >
                <i className="ri-download-2-line mr-2"></i>{t('hero.appBtn')}
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-6 md:gap-10 bg-background-50/10 backdrop-blur-md rounded-full px-6 md:px-10 py-3 md:py-4 border border-background-50/20">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-background-50 text-lg md:text-2xl font-bold font-display">
                  {stat.value}{stat.suffix}
                </p>
                <p className="text-background-50/60 text-[10px] md:text-xs mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900">{t('home.categories')}</h2>
            <p className="text-sm text-foreground-500 mt-1">{t('home.categoriesDesc')}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-lg bg-background-100 hover:bg-accent-50 transition-all duration-200 no-underline border border-background-200/60 hover:border-accent-200"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-200">
                <i className={`${cat.icon} text-xl md:text-2xl`}></i>
              </div>
              <span className="text-sm font-semibold text-foreground-900 group-hover:text-accent-600 transition-colors">
                {cat.name}
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900">{t('home.featured')}</h2>
              <p className="text-sm text-foreground-500 mt-1">{t('home.featuredDesc')}</p>
            </div>
            <Link
              to="/category/dianshiju"
              className="whitespace-nowrap text-sm text-primary-500 hover:text-primary-600 font-medium no-underline transition-colors"
            >
              {t('home.viewAll')} <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
            {homeVideos.map((video) => (
              <VideoCard key={video.id} {...video} />
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900">{t('home.latestArticles')}</h2>
            <p className="text-sm text-foreground-500 mt-1">{t('home.latestArticlesDesc')}</p>
          </div>
          <Link
            to="/articles"
            className="whitespace-nowrap text-sm text-primary-500 hover:text-primary-600 font-medium no-underline transition-colors"
          >
            {t('home.viewAll')} <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>

        <div className="flex flex-col gap-4">
          {latestArticles.map((article) => (
            <ArticleCard key={article.id} {...article} />
          ))}
        </div>
      </section>

      <section className="relative py-16 md:py-20 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20cinematic%20gradient%20background%20with%20warm%20orange%20and%20deep%20charcoal%20flowing%20abstract%20shapes%2C%20luxurious%20velvet%20texture%2C%20soft%20glowing%20highlights%2C%20theater%20curtain%20like%20curves%2C%20elegant%20atmospheric%20design&width=1920&height=600&seq=cta-bg-home-01&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-secondary-900/90 to-secondary-900/80"></div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-background-50">{t('home.cta.title')}</h2>
          <p className="text-background-50/70 text-sm md:text-base mt-3 max-w-xl mx-auto">
            {t('home.cta.subtitle')}
          </p>
          <Link
            to="/category/dianshiju"
            className="whitespace-nowrap inline-flex items-center bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3.5 rounded-full text-sm font-semibold mt-6 transition-all duration-200 no-underline"
          >
            {t('home.cta.button')} <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>
    </>
  );
}